import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { forgotPassword } from '~/services/account';
import { TextInput } from '~/shared/components/form/Inputs';
import { Container, Button, Card, CardBody, Col, Row, Form } from 'reactstrap';
import { useToasts } from 'react-toast-notifications';
import { renderIcon } from '~/shared/components/icons';
import { AuthContext } from '~/context/auth';

type FormData = {
    email: string;
};

export const ForgotPassword = ({}: any) => {
    const [{ user }, userDispatch] = useContext(AuthContext);
    const [saving, setSaving] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const { addToast } = useToasts();

    const history = useHistory();
    const { handleSubmit, control } = useForm<FormData>({
        defaultValues: {
            email: '',
        },
    });

    useEffect(() => {
        // Clear authentication token and user when visiting forgot password page
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Clear user from context
        userDispatch({
            type: 'set_user',
            user: null,
        });
    }, []);

    const onSubmit = async (data: FormData) => {
        const errMessage =
            'An unexpected error occurred. Please try again later.';
        try {
            const { email } = data;
            setSaving(true);
            setEmail(email);
            localStorage.removeItem('token');
            const res = await forgotPassword(email);
            setSaving(false);
            if (res.detail !== 'Not found.') setShowConfirmation(true);
            else {
                addToast(errMessage, {
                    appearance: 'error',
                });
            }
        } catch (error) {
            addToast(errMessage, {
                appearance: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const PasswordResetForm = () => {
        return (
            <>
                <h3 className='mb-5'>Forgot Password</h3>
                <p>
                    Enter the email address associated with your Help Hope Live
                    profile and click Send Email. You will receive an email from
                    Help Hope Live with a link to create a new password.
                </p>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <TextInput
                        name='email'
                        label='Your Email'
                        type='email'
                        control={control}
                        required={true}
                        ariaRequired={true}
                        maxLength={255}
                    />
                    <br />
                    <Button
                        className='me-0'
                        color='primary'
                        type='submit'
                        disabled={saving}
                        aria-label='Send password reset email'
                    >
                        {saving ? `Sending Email` : `Send Email`}
                    </Button>
                    <br />
                    <br />
                    <Button
                        color='link'
                        onClick={() => history.push('/')}
                        aria-label='Navigate back to login page'
                    >
                        Return to Login
                    </Button>
                </Form>
            </>
        );
    };

    const Confirmation = ({ email }: { email: string }) => {
        return (
            <>
                <div className='mb-3' style={{ fontSize: '40px' }}>
                    {renderIcon('success')}
                </div>
                <h3 className='mb-5'>Reset Password Email Sent</h3>
                <p>
                    We have sent a password reset email to{' '}
                    <span className='fw-bold'>{email}</span>. Please check your
                    Spam folder.
                </p>
                <Row>
                    <Button
                        color='link'
                        onClick={() => history.push('/')}
                        aria-label='Navigate back to login page'
                    >
                        Return to Login
                    </Button>
                </Row>
            </>
        );
    };

    return (
        <div className='vh-100 d-flex flex-column justify-content-center'>
            <div>
                <a
                    className='sidebar__logo mx-auto mb-5'
                    style={{ width: '316px', height: '120px' }}
                    href={import.meta.env.VITE_WP_URL}
                />
            </div>
            <div>
                <Container className='w-100' style={{ maxWidth: '960px' }}>
                    <Row className='justify-content-center'>
                        <Col md='6'>
                            <Card className='border border-primary'>
                                <CardBody className='p-5 text-center'>
                                    {!showConfirmation ? (
                                        <PasswordResetForm />
                                    ) : (
                                        <Confirmation email={email} />
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    );
};
